import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { JsonTree } from './components/JsonTree';
import { getStagedPackageSummary } from './hooks/usePackages';

// ── Types ──────────────────────────────────────────────────────────────────────

interface FacilitySummary {
  id: number;
  name?: string;
  license?: string;
}

interface OrganizationSummary {
  id: number;
  name?: string;
}

interface PrintRunItem {
  id: number;
  jobName?: string;
  jobId?: string;
  status: number;
  templateId: number;
  templateName?: string;
  packageLabel?: string;
  count: number;
  productName?: string;
  packagedDate?: string;
  printedDate?: string;
  createdAt: string;
}


interface StagedPackageItem {
  packageLabel: string;
  tagId: number;
  facilityId: number;
  createdAt: string;
  data: string;
  printRunId?: number;
  productId?: number;
  productName?: string;
  sourcePackageId?: number;
  coaFilesIds?: number[];
  coaFiles?: string[];
  coaFileNames?: string[];
  lastModified?: string;
}

interface StagedPackageSummaryItem {
  stagedPackage: StagedPackageItem;
  organization?: OrganizationSummary;
  facility?: FacilitySummary;
  printRuns?: PrintRunItem[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────
// ── Constants ──────────────────────────────────────────────────────────────────

const PRINT_STATUS: Record<number, string> = {
  0: 'Queued',
  1: 'In Progress',
  2: 'Completed',
  3: 'Failed',
};

const PRINT_STATUS_COLOR: Record<number, { bg: string; text: string }> = {
  0: { bg: '#f1f3f4', text: '#666' },
  1: { bg: '#fff3e0', text: '#b45309' },
  2: { bg: '#e6f4ea', text: '#22863a' },
  3: { bg: '#fde8e8', text: '#cb2431' },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
function PrintRunsTable({ printRuns }: { printRuns: PrintRunItem[] }) {
  if (printRuns.length === 0) {
    return (
      <p className="muted" style={{ padding: '0.5rem 0.25rem', fontSize: '0.83rem' }}>
        No print runs.
      </p>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table" style={{ fontSize: '0.82rem' }}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Job Name</th>
            <th>Template</th>
            <th>Status</th>
            <th>Count</th>
            <th>Printed</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {printRuns.map((p, i) => {
            const colors = PRINT_STATUS_COLOR[p.status] ?? PRINT_STATUS_COLOR[0];
            return (
              <tr key={p.id} style={{ background: i % 2 === 0 ? 'white' : '#f8f9fa' }}>
                <td style={{ fontFamily: 'monospace', color: '#888' }}>{p.id}</td>
                <td>{p.jobName || '—'}</td>
                <td>{p.templateName || p.templateId}</td>
                <td>
                  <span className="badge" style={{ background: colors.bg, color: colors.text }}>
                    {PRINT_STATUS[p.status] ?? p.status}
                  </span>
                </td>
                <td>{p.count}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.printedDate) || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


function fmtDate(val: string | null | undefined): string {
  if (!val) return '';
  try {
    return new Date(val).toLocaleString();
  } catch {
    return val;
  }
}

function fmtVal(val: unknown): string | null {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
}

function hasDisplayValue(val: unknown): boolean {
  if (val === null || val === undefined || val === '') return false;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === 'object') return Object.keys(val as object).length > 0;
  return true;
}

function pruneEmptyValue(val: unknown): unknown {
  if (val === null || val === undefined || val === '') return null;

  if (Array.isArray(val)) {
    const pruned = val
      .map((item) => pruneEmptyValue(item))
      .filter((item) => item !== null);
    return pruned.length > 0 ? pruned : null;
  }

  if (typeof val === 'object') {
    const entries = Object.entries(val as Record<string, unknown>)
      .map(([k, v]) => [k, pruneEmptyValue(v)] as const)
      .filter(([, v]) => v !== null);

    if (entries.length === 0) return null;
    return Object.fromEntries(entries);
  }

  return val;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function KvTable({ rows }: { rows: Array<[string, unknown]> }) {
  const visible = rows.filter(([, v]) => fmtVal(v) !== null);
  if (visible.length === 0) return null;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
      <tbody>
        {visible.map(([label, value], i) => (
          <tr key={label} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#f8f9fa' }}>
            <td
              style={{
                padding: '6px 10px',
                color: '#888',
                fontWeight: 500,
                width: '38%',
                verticalAlign: 'top',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </td>
            <td style={{ padding: '6px 10px', verticalAlign: 'top' }}>{fmtVal(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Section({

  title,
  count,
  defaultOpen = true,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: '1rem' }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} node ${title}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: '100%',
          background: '#c3c4c5',
          border: '1px solid #e8e8e8',
          borderRadius: '4px',
          cursor: 'pointer',
          padding: '0.5rem 0.75rem',
          fontWeight: 600,
          fontSize: '0.84rem',
          color: 'var(--brand)',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '0.6rem' }}>{open ? '▼' : '▶'}</span>
        <span style={{ flex: 1 }}>{title}</span>
        {count !== undefined && (
          <span
            style={{
              background: count > 0 ? 'var(--brand)' : '#bbb',
              color: 'white',
              borderRadius: '999px',
              padding: '1px 8px',
              fontSize: '0.72rem',
              fontWeight: 700,
              minWidth: '20px',
              textAlign: 'center',
            }}
          >
            {count}
          </span>
        )}
      </button>
      {open && <div style={{ paddingTop: '0.25rem' }}>{children}</div>}
    </div>
  );
}

function StagedCoaCard({ item, index }: { item: StagedPackageSummaryItem; index: number }) {
  const [open, setOpen] = useState(true);
  const pkg = item.stagedPackage;
  let parsedData: unknown = pkg.data;

  while (typeof parsedData === 'string') {
    try {
      parsedData = JSON.parse(parsedData);
    } catch {
      break;
    }
  }

  const prunedCoaData = pruneEmptyValue(parsedData);
  const hasFiles = !!pkg.coaFiles && pkg.coaFiles.length > 0;
  const hasCoaData = hasDisplayValue(prunedCoaData);

  const infoRows: Array<[string, unknown]> = [
    ['Tag ID', pkg.tagId],
    ['Facility ID', pkg.facilityId],
    ['Product ID', pkg.productId],
    ['Source Package ID', pkg.sourcePackageId],
    ['Print Run ID', pkg.printRunId],
    ['Created At', fmtDate(pkg.createdAt)],
    ['Last Modified', fmtDate(pkg.lastModified)],
  ];
  const hasInfo = infoRows.some(([, v]) => fmtVal(v) !== null);

  const organizationRows: Array<[string, unknown]> = [
    ['ID', item.organization?.id],
    ['Name', item.organization?.name],
  ];
  const hasOrganization = item.organization && organizationRows.filter(([, v]) => hasDisplayValue(v)).length > 0;
  const organizationLabel = item.organization
    ? `Org: ${item.organization.name || 'Unknown'} (ID ${item.organization.id})`
    : null;

  const facilityRows: Array<[string, unknown]> = [
    ['ID', item.facility?.id],
    ['Name', item.facility?.name],
    ['License', item.facility?.license],
  ];
  const hasFacility = item.facility && facilityRows.filter(([, v]) => hasDisplayValue(v)).length > 0;

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      {/* Card header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: '1rem',
              fontFamily: 'monospace',
              letterSpacing: '0.02em',
            }}
          >
            {pkg.packageLabel}
          </div>
          {pkg.productName && (
            <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.15rem' }}>
              {pkg.productName}
            </div>
          )}
        </div>
        {organizationLabel && (
          <span className="badge" style={{ background: '#eef6ff', color: '#075985', fontWeight: 700 }}>
            {organizationLabel}
          </span>
        )}
        <div style={{ fontSize: '0.8rem', color: '#999', whiteSpace: 'nowrap' }}>
          Entry #{index + 1} · {fmtDate(pkg.createdAt)}
        </div>
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          style={{
            background: 'none',
            border: '1px solid #e8e8e8',
            borderRadius: '4px',
            cursor: 'pointer',
            padding: '0.25rem 0.6rem',
            fontSize: '0.75rem',
            color: 'var(--brand)',
          }}
        >
          {open ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {open && (
        <>
          {hasInfo && (
            <Section title="Info" defaultOpen={true}>
              <KvTable rows={infoRows} />
            </Section>
          )}

          {hasOrganization && (
            <Section title="Organization" defaultOpen={true}>
              <KvTable rows={organizationRows} />
            </Section>
          )}

          {hasFiles && (
            <Section title="Attached Files" count={pkg.coaFiles!.length} defaultOpen={true}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.3rem',
                  padding: '0.4rem 0.25rem',
                }}
              >
                {pkg.coaFiles!.map((url, i) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '0.83rem', color: 'var(--brand)' }}
                  >
                    {pkg.coaFileNames?.[i] || url}
                  </a>
                ))}
              </div>
            </Section>
          )}

          {hasFacility && (
            <Section title="Facility" defaultOpen={true}>
              <KvTable rows={facilityRows} />
            </Section>
          )}

          {item.printRuns && item.printRuns.length > 0 && (
            <Section title="Print Runs" count={item.printRuns.length} defaultOpen={true}>
              <PrintRunsTable printRuns={item.printRuns} />
            </Section>
          )}

          {hasCoaData && (
            <Section title="COA Data" defaultOpen={true}>
              <div
                style={{
                  background: '#fafafa',
                  border: '1px solid #e4e8ec',
                  borderRadius: '4px',
                  padding: '0.75rem',
                  marginTop: '0.25rem',
                }}
              >
                <JsonTree value={prunedCoaData} />
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function ViewToggle({
  mode,
  onChange,
}: {
  mode: 'structured' | 'raw';
  onChange: (m: 'structured' | 'raw') => void;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <button
        type="button"
        className={`button${mode === 'structured' ? ' cta' : ''}`}
        onClick={() => onChange('structured')}
      >
        Structured
      </button>
      <button
        type="button"
        className={`button${mode === 'raw' ? ' cta' : ''}`}
        onClick={() => onChange('raw')}
      >
        Raw JSON
      </button>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function StagedPackageSummaryPage() {
  const navigate = useNavigate();
  const [label, setLabel] = useState('');
  const [result, setResult] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [viewMode, setViewMode] = useState<'structured' | 'raw'>('structured');

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setNotFound(false);

    try {
      const data = await getStagedPackageSummary(trimmed);
      setResult(data);
    } catch (err: unknown) {
      if ((err as any)?.response?.status === 404 || (err as any)?.status === 404) {
        setNotFound(true);
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const items = Array.isArray(result) ? (result as StagedPackageSummaryItem[]) : null;
  const hasRenderableItems = !items || items.length > 0;

  return (
    <div className="stack" data-testid="page-staged-package-summary">
      <PageHeader
        title="Staged Package Summary"
        subtitle="Look up a staged package summary by label"
        actions={
          <button type="button" className="button" onClick={() => navigate('/internal')}>
            Back
          </button>
        }
      />

      <div className="card" style={{ padding: '2rem' }}>
        <form className="stack" onSubmit={handleSearch}>
          <label htmlFor="staged-package-label-input" style={{ fontWeight: 600 }}>
            Package Label
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              id="staged-package-label-input"
              type="text"
              className="input"
              placeholder="Enter package label…"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={loading}
              style={{ flex: 1 }}
              data-testid="staged-package-label-input"
            />
            <button
              type="submit"
              className="button cta"
              disabled={loading || !label.trim()}
              data-testid="staged-package-summary-search-btn"
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="card" style={{ padding: '2rem' }}>
          <p style={{ color: 'var(--error, red)' }} data-testid="staged-package-summary-error">
            {error}
          </p>
        </div>
      )}

      {(notFound || (result !== null && Array.isArray(result) && result.length === 0)) && (
        <div
          className="card"
          style={{ padding: '1.5rem', textAlign: 'center', color: '#888', fontSize: '0.9rem' }}
          data-testid="staged-package-summary-no-results"
        >
          No results found for label <strong>{label}</strong>.
        </div>
      )}

      {result !== null && hasRenderableItems && (
        <div className="stack">
          <ViewToggle mode={viewMode} onChange={setViewMode} />

          {viewMode === 'structured' ? (
            items ? (
              <>
                {items.length > 1 && (
                  <div
                    className="card"
                    style={{ padding: '0.75rem 1.25rem', fontSize: '0.85rem', color: '#666' }}
                  >
                    {items.length} staged entries found for label <strong>{label}</strong>
                  </div>
                )}
                {items.map((item, i) => (
                  <StagedCoaCard key={i} item={item} index={i} />
                ))}
              </>
            ) : (
              <div className="card" style={{ padding: '1.5rem' }}>
                <JsonTree value={result} />
              </div>
            )
          ) : (
            <div className="card" style={{ padding: '2rem' }}>
              <textarea
                id="staged-package-summary-output"
                readOnly
                value={JSON.stringify(result, null, 2)}
                rows={24}
                style={{
                  width: '100%',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  resize: 'vertical',
                }}
                data-testid="staged-package-summary-output"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
